/**
 * Isochrone Service
 * Handles fetching isochrone data from external APIs (Mapbox Isochrone API).
 */

import type { Coordinates, TravelMode } from '../types';
import { validateCoordinates, validateTravelTime } from '../utils/validation';
import { getApiConfig } from '../config/api';
import { isochroneCache } from '../utils/cacheManager';
import { canMakeCalculation, trackCalculation } from './calculationService';

/**
 * Retry configuration for API calls.
 */
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

/**
 * Sleeps for a specified number of milliseconds.
 * @param ms - Milliseconds to sleep
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retries a function with exponential backoff.
 * @param fn - Function to retry
 * @param retries - Number of retries remaining
 * @param delay - Current retry delay in milliseconds
 * @returns Result of the function
 * @throws Error if all retries fail
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  retries: number = MAX_RETRIES,
  delay: number = INITIAL_RETRY_DELAY
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) {
      throw error;
    }
    await sleep(delay);
    return retryWithBackoff(fn, retries - 1, delay * 2);
  }
}

/**
 * Fetches a single isochrone from the Mapbox Isochrone API.
 * Checks cache first to avoid unnecessary API calls and usage counter increments.
 * @param origin - Origin coordinates
 * @param time - Travel time in minutes
 * @param mode - Travel mode (driving, walking, cycling, transit)
 * @param userId - Optional user ID for usage tracking (only tracked if provided)
 * @returns Promise resolving to GeoJSON polygon representing the isochrone
 * @throws Error if API call fails, coordinates are invalid, or usage limit exceeded
 */
export async function fetchIsochrone(
  origin: Coordinates,
  time: number,
  mode: TravelMode,
  userId?: string
): Promise<GeoJSON.Polygon> {
  // Validate inputs
  if (!validateCoordinates(origin)) {
    throw new Error('Invalid coordinates');
  }
  if (!validateTravelTime(time)) {
    throw new Error('Invalid travel time');
  }

  // Generate cache key
  const cacheKey = isochroneCache.generateCacheKey(origin, time, mode);

  // Check cache first
  if (isochroneCache.has(cacheKey)) {
  // Cache hit - no need to log (too noisy for each API call)
    return isochroneCache.get(cacheKey)!;
  }

  // Cache miss - no need to log (too noisy for each API call)

  // Check usage limit if user is authenticated
  if (userId) {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('[fetchIsochrone] userId provided:', userId);
    }
    const canCalculate = await canMakeCalculation(userId);
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('[fetchIsochrone] canMakeCalculation result:', canCalculate);
    }
    if (!canCalculate) {
      throw new Error('USAGE_LIMIT_EXCEEDED');
    }
  }
  // Note: When userId is not provided (e.g., called from fetchIsochroneRange),
  // tracking is handled at the range level to avoid duplicate tracking

  // Map travel mode to Mapbox profile
  const profileMap: Record<TravelMode, string> = {
    driving: 'driving',
    walking: 'walking',
    cycling: 'cycling',
    transit: 'driving', // Mapbox uses 'driving' profile for transit
  };

  const profile = profileMap[mode];

  // Build API URL
  const config = getApiConfig();
  const { lng, lat } = origin;
  const url = `${config.isochroneApiUrl}/${profile}/${lng},${lat}?contours_minutes=${time}&access_token=${config.mapboxToken}`;

  // Make API call and get polygon
  const startTime = performance.now();
  const polygon = await retryWithBackoff(async () => {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      
      // Track API error in PostHog
      (async () => {
        const { trackEvent } = await import('./posthogService');
        trackEvent('isochrone_api_error', {
          status: response.status,
          status_text: response.statusText,
          error_text: errorText.substring(0, 200), // Limit error text length
          origin,
          time,
          mode,
        });
      })();
      
      throw new Error(
        `Isochrone API error: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    const data = await response.json();

    // Debug: log the response structure
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('Isochrone API response:', {
        hasFeatures: !!data.features,
        featureCount: data.features?.length || 0,
        firstFeatureGeometryType: data.features?.[0]?.geometry?.type,
      });
    }

    // Extract the polygon from the GeoJSON Feature
    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      
      // Handle different geometry types
      if (feature.geometry.type === 'Polygon') {
        return feature.geometry as GeoJSON.Polygon;
      } else if (feature.geometry.type === 'MultiPolygon') {
        // Convert MultiPolygon to Polygon (use the first polygon)
        const multiPolygon = feature.geometry as GeoJSON.MultiPolygon;
        if (multiPolygon.coordinates.length > 0) {
          return {
            type: 'Polygon',
            coordinates: multiPolygon.coordinates[0],
          } as GeoJSON.Polygon;
        }
      } else if (feature.geometry.type === 'LineString') {
        // Convert LineString to Polygon by closing the ring
        const lineString = feature.geometry as GeoJSON.LineString;
        const coordinates = lineString.coordinates;
        
        // Close the ring by adding the first coordinate at the end
        const closedCoordinates = [...coordinates];
        if (coordinates.length > 0) {
          const firstCoord = coordinates[0];
          const lastCoord = coordinates[coordinates.length - 1];
          
          // Only close if not already closed
          if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
            closedCoordinates.push([firstCoord[0], firstCoord[1]]);
          }
        }
        
        return {
          type: 'Polygon',
          coordinates: [closedCoordinates],
        } as GeoJSON.Polygon;
      }
      
      // Log what we got for debugging
      console.error('Unexpected geometry type:', {
        type: feature.geometry.type,
        feature: feature,
      });
      
      throw new Error(`Invalid geometry type in API response: ${feature.geometry.type}. Expected Polygon, MultiPolygon, or LineString.`);
    }

    throw new Error('No features found in API response');
  });

  // Store in cache AFTER successful API call
  isochroneCache.set(cacheKey, polygon);
  
  // Track successful API call in PostHog
  const duration = performance.now() - startTime;
  (async () => {
    const { trackEvent } = await import('./posthogService');
    trackEvent('isochrone_api_success', {
      origin,
      time,
      mode,
      duration_ms: Math.round(duration),
      from_cache: false,
    });
  })();

  // Track calculation if user is authenticated (non-blocking, silent)
  if (userId) {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('[fetchIsochrone] Starting trackCalculation for userId:', userId);
    }
    // Don't await - track in background to avoid blocking UI
    trackCalculation({
      userId,
      originLat: origin.lat,
      originLng: origin.lng,
      maxTime: time,
      travelMode: mode,
    }).then(() => {
      if (import.meta.env.VITE_DEBUG === 'true') {
        console.log('[fetchIsochrone] trackCalculation SUCCESS for time:', time);
      }
    }).catch((error) => {
      // Silently handle errors - log but don't block
      if (error instanceof Error && error.message === 'USAGE_LIMIT_EXCEEDED') {
        // Log for debugging, but don't re-throw (background operation)
        console.error('[trackCalculation] Limit exceeded during background tracking');
      } else {
        console.error('[trackCalculation] Failed:', error);
      }
    });
  }

  return polygon;
}

/**
 * Fetches multiple isochrones for a range of travel times.
 * Checks cache for each time value and only makes API calls for cache misses.
 * @param origin - Origin coordinates
 * @param times - Array of travel times in minutes
 * @param mode - Travel mode
 * @param userId - Optional user ID for usage tracking (only tracked if provided)
 * @returns Promise resolving to Map of time to polygon
 * @throws Error if API calls fail or usage limit exceeded
 */
export async function fetchIsochroneRange(
  origin: Coordinates,
  times: number[],
  mode: TravelMode,
  userId?: string
): Promise<Map<number, GeoJSON.Polygon>> {
  // Validate and sort times
  const validTimes = times
    .filter(validateTravelTime)
    .sort((a, b) => a - b);

  if (validTimes.length === 0) {
    throw new Error('No valid travel times provided');
  }

  // Track this as ONE calculation (not per time interval)
  // We track BEFORE fetching so we know if any are cache misses
  let hasAnyCacheMiss = false;

  // Check which times will be cache misses
  for (const time of validTimes) {
    const cacheKey = isochroneCache.generateCacheKey(origin, time, mode);
    if (!isochroneCache.has(cacheKey)) {
      hasAnyCacheMiss = true;
      break;
    }
  }

  // If there's any cache miss and user is authenticated, check limit and track ONCE
  if (hasAnyCacheMiss && userId) {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('[fetchIsochroneRange] Cache miss detected, checking limit and tracking calculation');
    }
    const canCalculate = await canMakeCalculation(userId);
    if (!canCalculate) {
      throw new Error('USAGE_LIMIT_EXCEEDED');
    }

    // Track the calculation ONCE for the entire range
    await trackCalculation({
      userId,
      originLat: origin.lat,
      originLng: origin.lng,
      maxTime: Math.max(...validTimes), // Use the max time as the calculation time
      travelMode: mode,
    });
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.log('[fetchIsochroneRange] Calculation tracked successfully');
    }
  }

  // Fetch all isochrones in parallel (pass userId=undefined to prevent duplicate tracking)
  const fetchPromises = validTimes.map(async (time) => {
    try {
      // Pass undefined for userId to prevent tracking in individual fetchIsochrone calls
      const polygon = await fetchIsochrone(origin, time, mode, undefined);
      return { time, polygon };
    } catch (error) {
      console.error(`Failed to fetch isochrone for ${time} minutes:`, error);
      throw error;
    }
  });

  const results = await Promise.allSettled(fetchPromises);

  // Build result map
  const resultMap = new Map<number, GeoJSON.Polygon>();

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      resultMap.set(result.value.time, result.value.polygon);
    } else {
      console.error(
        `Failed to fetch isochrone for ${validTimes[index]} minutes:`,
        result.reason
      );
    }
  });

  if (resultMap.size === 0) {
    throw new Error('Failed to fetch any isochrones');
  }

  return resultMap;
}

